// Shared JS CONSUMER adapter for the generated typography catalog (B11 —
// typography de-mirror).
//
// The typography data (font pairs, weights, italic / uppercase / letter-spacing
// per named pair) is authored ONCE in the Remotion TS renderer
// (remotion-service/templates/src/<t>/typography.ts, or a shared family source
// under src/shared/typography/) and emitted to a checked-in machine-readable
// JSON by generate-typography-catalog.mjs. Per-template / per-family preview
// mirrors (e.g. daily_quote_type_pairs.js, finance_type_pairs.js) import that
// JSON and pass it through `toJsSpecs` — they no longer restate the data, so the
// JS preview surface can never drift from what the renderer paints.
//
// PROFILES — the catalog JSON is a RICH SUPERSET (family + fallback + weight +
// italic + letterSpacing + textTransform per role). Different consumer families
// want different projections of it. A `profile` selects which keys are emitted
// and whether the fallback is appended to the font family:
//
//   rich  (Daily Quote / Book-Takeaway-style pickers): font WITH the CSS generic
//         fallback appended ('"Playfair Display", serif'); `Uppercase` +
//         `LetterSpacing` ALWAYS present (null when unset).
//   bare  (language-learning / finance / quiz families): font family ALONE
//         ("Lora"); no `Uppercase`; `LetterSpacing` only when a pair actually
//         sets it. This is the historical hand-mirrored family shape.
//
// `toJsSpecs(catalog)` defaults to `rich` so the Daily Quote pilot stays byte-
// identical.

// The 10 keys a RICH JS-shaped spec declares — matches the Ruby picker shape
// (display/body font, weight, italic, uppercase, letterSpacing). Consumed by the
// Daily Quote drift spec and any rich preview controller reading a spec.
export const REQUIRED_SPEC_KEYS = [
  "displayFont",
  "displayWeight",
  "displayItalic",
  "displayUppercase",
  "displayLetterSpacing",
  "bodyFont",
  "bodyWeight",
  "bodyItalic",
  "bodyUppercase",
  "bodyLetterSpacing",
]

// The 6 keys every BARE family spec declares — no uppercase, no always-present
// letterSpacing. Consumed by the language-learning / finance family drift specs.
export const BARE_REQUIRED_SPEC_KEYS = [
  "displayFont",
  "displayWeight",
  "displayItalic",
  "bodyFont",
  "bodyWeight",
  "bodyItalic",
]

// Profile presets. `fallback` appends the generic silhouette to the font family;
// `uppercase` / `letterSpacing` control whether (and when) those keys are
// emitted per role.
const PROFILES = {
  rich: { fallback: true, uppercase: "always", letterSpacing: "always" },
  bare: { fallback: false, uppercase: "omit", letterSpacing: "whenPresent" },
}

// One atomic role entry ({ family, fallback, weight, italic, letterSpacing,
// textTransform }) -> the JS CSS-oriented fields, prefixed `display`/`body`,
// projected through `profile`. Rich `Font` composes '"Family", fallback' (quoted
// family, CSS idiom the JS preview wants); bare `Font` is the family alone.
function roleSpec(prefix, entry, profile) {
  const out = {}
  out[`${prefix}Font`] = profile.fallback ? `"${entry.family}", ${entry.fallback}` : entry.family
  out[`${prefix}Weight`] = entry.weight
  out[`${prefix}Italic`] = Boolean(entry.italic)

  const uppercase = entry.textTransform === "uppercase"
  if (profile.uppercase === "always") {
    out[`${prefix}Uppercase`] = uppercase
  } else if (profile.uppercase === "whenTrue" && uppercase) {
    out[`${prefix}Uppercase`] = true
  }

  const ls = entry.letterSpacing ?? null
  if (profile.letterSpacing === "always") {
    out[`${prefix}LetterSpacing`] = ls
  } else if (profile.letterSpacing === "whenPresent" && ls !== null) {
    out[`${prefix}LetterSpacing`] = ls
  }
  return out
}

// Raw catalog doc ({ template, pairs: { <key>: { display, body } } }) -> the
// JS-shaped TYPE_PAIR_SPECS map. `profile` selects the projection (default
// "rich" so existing consumers are byte-identical).
export function toJsSpecs(rawCatalog, { profile = "rich" } = {}) {
  const p = PROFILES[profile] || PROFILES.rich
  const specs = {}
  for (const [key, pair] of Object.entries(rawCatalog.pairs)) {
    specs[key] = { ...roleSpec("display", pair.display, p), ...roleSpec("body", pair.body, p) }
  }
  return specs
}

// Resolve a length-tiered font size (px) from a catalog font-size config
// ({ basePx, tiers: [{ maxLength, scale }] }) — mirrors resolveQuoteFontPx in
// the TS source so the wizard preview size matches the rendered video. A
// `maxLength: null` tier is the open-ended catch-all.
export function resolveTieredFontPx(config, text) {
  const len = (text ?? "").length
  for (const tier of config.tiers) {
    if (tier.maxLength === null || len <= tier.maxLength) {
      return Math.round(config.basePx * tier.scale)
    }
  }
  return config.basePx
}
